import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { api } from "@/lib/api.js";
import type { CompanyListItem } from "@bo/shared";

export function CompaniesPage() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data, isLoading } = useQuery({
    queryKey: ["companies", { q, page }],
    queryFn: () =>
      api.get<{ items: CompanyListItem[]; total: number }>(
        `/companies?page=${page}&pageSize=${pageSize}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
      ),
  });

  const columns: Column<CompanyListItem>[] = [
    {
      header: "ID",
      width: "90px",
      render: (r) => <span className="mono text-xs">{r.idcompany}</span>,
    },
    {
      header: "Nombre",
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    { header: "País", render: (r) => r.country ?? "—" },
    { header: "Distribuidor", render: (r) => r.plan ?? "—" },
    { header: "Canales", render: (r) => r.channelCount, width: "90px" },
    {
      header: "Estado",
      render: (r) => <StatusBadge value={r.status} />,
      width: "120px",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Companies"
        description="Directorio de clientes (tenants) de Gina."
      />
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            className="input pl-9"
            placeholder="Buscar por ID o nombre…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        getKey={(r) => r.idcompany}
        onRowClick={(r) => nav(`/companies/${r.idcompany}`)}
        empty="Sin companies que coincidan."
      />
      {data && data.total > pageSize && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} de {data.total}
          </span>
          <div className="flex gap-2">
            <button
              className="btn-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </button>
            <button
              className="btn-secondary"
              disabled={page * pageSize >= data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
      {data && (
        <p className="text-xs text-slate-400">
          Tip: click en una fila para ver el detalle de la company.
        </p>
      )}
      <ul className="sr-only">
        {data?.items.map((c) => (
          <li key={c.idcompany}>
            <Link to={`/companies/${c.idcompany}`}>{c.name}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
